import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMessaging } from '../contexts/MessagingContext';
import Logo from './Logo';

/**
 * Navbar component - Top navigation bar with authentication state and navigation options
 */
const Navbar = () => {
  const { user, signOut, isAuthenticated } = useAuth();
  const { unreadCount } = useMessaging();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const navigate = useNavigate();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMoreMenuOpen) setIsMoreMenuOpen(false);
  };
  
  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };
  
  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };
  
  return (
    <nav className="bg-primary-600 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Logo />
            </div>
            
            {/* Desktop primary navigation links */}
            <div className="hidden md:ml-6 md:flex md:items-center md:space-x-2">
              <Link to="/gallery" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 flex items-center">Gallery</Link>
              <Link to="/profiles" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 flex items-center">Profiles</Link>
              <Link to="/resources" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 flex items-center">Resources</Link>
              <Link to="/about" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 flex items-center">About</Link>
              <Link to="/contact" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 flex items-center">Contact</Link>
              
              {/* Authenticated user menu */}
              {isAuthenticated && (
                <div className="hidden md:ml-6 md:flex md:items-center">
                  <div className="relative">
                    <button
                      onClick={toggleMoreMenu}
                      className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 focus:outline-none"
                      aria-expanded={isMoreMenuOpen}
                    >
                      Platform <span className="ml-1">▼</span>
                    </button>
                    
                    {isMoreMenuOpen && (
                      <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1" role="menu" aria-orientation="vertical">
                          <Link 
                            to="/dashboard" 
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsMoreMenuOpen(false)}
                          >
                            Dashboard
                          </Link>
                          <Link 
                            to="/messaging" 
                            className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsMoreMenuOpen(false)}
                          >
                            <span>Messaging</span>
                            {unreadCount > 0 && (
                              <span className="inline-flex items-center justify-center ml-2 h-5 w-5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                {unreadCount}
                              </span>
                            )}
                          </Link>
                          <Link 
                            to="/products" 
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsMoreMenuOpen(false)}
                          >
                            Products
                          </Link>
                          <Link 
                            to="/management" 
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsMoreMenuOpen(false)}
                          >
                            Management
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Right side buttons */}
          <div className="flex items-center">
            {isAuthenticated ? (
              <div className="hidden md:flex items-center">
                {/* Messages icon with notification indicator (for desktop) */}
                <Link to="/messaging" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 mr-2 relative">
                  <div className="flex items-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 w-5 text-xs font-medium rounded-full bg-red-600 text-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                </Link>
                
                <Link to="/profile" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 mr-2">
                  {user?.profile?.name && !user?.profile?.name.includes('@') ? 
                    user.profile.name : 'Profile'}
                </Link>
                <button
                  onClick={handleSignOut}
                  className="px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="hidden md:flex space-x-2">
                <Link to="/signin" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700">Sign In</Link>
                <Link to="/signup" className="px-3 py-2 rounded-md bg-white text-primary-600 font-medium hover:bg-gray-100">
                  Sign Up
                </Link>
              </div>
            )}
            
            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              {isAuthenticated && (
                <Link to="/messaging" className="px-3 py-2 rounded-md font-medium hover:bg-primary-700 mr-1 relative">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 w-5 text-xs font-medium rounded-full bg-red-600 text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}
            
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md hover:bg-primary-700 focus:outline-none"
                aria-expanded={isMenuOpen ? 'true' : 'false'}
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg
                  className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-primary-700 border-t border-primary-500">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link 
              to="/gallery" 
              className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
              onClick={() => setIsMenuOpen(false)}
            >
              Gallery
            </Link>
            
            <Link 
              to="/profiles" 
              className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
              onClick={() => setIsMenuOpen(false)}
            >
              Profiles
            </Link>
            
            <Link 
              to="/resources" 
              className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
              onClick={() => setIsMenuOpen(false)}
            >
              Resources
            </Link>
            
            <Link 
              to="/about" 
              className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            
            <Link 
              to="/contact" 
              className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
            
            {isAuthenticated && (
              <>
                <div className="pt-2 pb-1">
                  <div className="px-3 text-xs font-semibold text-primary-300 uppercase tracking-wider">
                    Platform
                  </div>
                </div>
                
                <Link 
                  to="/dashboard" 
                  className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/messaging" 
                  className="flex items-center justify-between px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span>Messaging</span>
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center ml-2 h-5 w-5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      {unreadCount}
                    </span>
                  )}
                </Link>
                <Link 
                  to="/products" 
                  className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Products
                </Link>
                <Link 
                  to="/management" 
                  className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Management
                </Link>
                
                <div className="pt-4 pb-1 border-t border-primary-500">
                  <div className="px-3 text-xs font-semibold text-primary-300 uppercase tracking-wider">
                    Account
                  </div>
                </div>
                
                <Link 
                  to="/profile" 
                  className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Your Profile
                </Link>
                <button
                  onClick={() => {
                    handleSignOut();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                >
                  Sign Out
                </button>
              </>
            )}
            
            {!isAuthenticated && (
              <>
                <div className="pt-4 pb-1 border-t border-primary-500">
                  <div className="px-3 text-xs font-semibold text-primary-300 uppercase tracking-wider">
                    Account
                  </div>
                </div>
                
                <Link 
                  to="/signin" 
                  className="block px-3 py-2 rounded-md font-medium hover:bg-primary-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link 
                  to="/signup" 
                  className="block px-3 py-2 rounded-md bg-white text-primary-600 font-medium hover:bg-gray-100 mt-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 